import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {TranslatedBankAccount} from '@lexian/api/invoicing-api';
import {useMasterdata} from '_/hooks/useMasterdata';
import {EFAutocomplete, EFAutocompleteProps} from '_/utilities/form';
import {getOptions} from '_/utilities/masterdata.utils';

import {
  useActiveFilters,
  UseActiveFiltersProps,
} from './ActiveFilter/ActiveFilter.hooks';
import {EFTagsAutocomplete} from './ETagsAutocomplete';

export const ClientMoneyBankAccountAutocomplete = ({
  label,
  onUserChange,
  multiple = false,
  onFilterChange,
  ...otherProps
}: Omit<EFAutocompleteProps, 'options'> &
  Pick<UseActiveFiltersProps, 'onFilterChange'> & {
    onUserChange?: (bankAccount: TranslatedBankAccount | undefined) => void;
    multiple?: boolean;
  }): React.ReactElement => {
  const {t} = useTranslation('common');
  const {translatedClientMoneyBankAccounts} = useMasterdata();

  const {
    activeOptions,
    showActiveFiltersDialog,
    activeFiltersDialog,
    optionFilterInfo,
  } = useActiveFilters({
    options: translatedClientMoneyBankAccounts,
    persistenceKey: 'client-money-bank-account-autocomplete',
    onFilterChange,
  });
  const options = getOptions(activeOptions);

  const onChangeHandler = useCallback(
    (
      e: React.ChangeEvent<any>,
      originalEventHandler: React.ChangeEventHandler
    ) => {
      if (onUserChange) {
        const selectedBankAccount = translatedClientMoneyBankAccounts.find(
          (bankAccount) => bankAccount.id === e.target.value
        );
        onUserChange(selectedBankAccount);
      }
      originalEventHandler(e);
    },
    [onUserChange, translatedClientMoneyBankAccounts]
  );

  return (
    <>
      {multiple ? (
        <EFTagsAutocomplete
          {...otherProps}
          customOnChange={onChangeHandler}
          label={label ?? t('common:labels.clientMoneyBankAccounts')}
          optionFilterInfo={optionFilterInfo}
          options={options}
          noOptionFiltering
          onFilterClick={showActiveFiltersDialog}
        />
      ) : (
        <EFAutocomplete
          {...otherProps}
          customOnChange={onChangeHandler}
          label={label ?? t('common:labels.clientMoneyBankAccount')}
          optionFilterInfo={optionFilterInfo}
          options={options}
          noOptionFiltering
          onFilterClick={showActiveFiltersDialog}
        />
      )}
      {activeFiltersDialog}
    </>
  );
};
